import React, { useState } from 'react';
import { X, Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { auth, googleProvider } from '../firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { useTranslation } from '../useTranslation';

const AuthModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setMessage('');
    setLoading(false);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const norm = (v) => v.trim();

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return t('auth.errors.userNotFound');
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return t('auth.errors.wrongPassword');
      case 'auth/email-already-in-use':
        return t('auth.errors.emailInUse');
      case 'auth/weak-password':
        return t('auth.errors.weakPassword');
      case 'auth/invalid-email':
        return t('auth.errors.invalidEmail');
      case 'auth/too-many-requests':
        return t('auth.errors.tooManyRequests');
      case 'auth/popup-closed-by-user':
        return t('auth.errors.popupClosed');
      case 'auth/cancelled-popup-request':
        return t('auth.errors.popupCancelled');
      case 'auth/popup-blocked':
        return t('auth.errors.popupBlocked');
      default:
        return t('auth.errors.generic');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithPopup(auth, googleProvider);
      handleClose();
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    const em = norm(email);
    const pw = password;

    if (!em || !pw) {
      setError(t('auth.errors.fillAll'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      await signInWithEmailAndPassword(auth, em, pw);
      handleClose();
    } catch (err) {
      console.error('Email sign-in error:', err);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    const em = norm(email);
    const pw = password;
    const cpw = confirmPassword;

    if (!em || !pw || !cpw) {
      setError(t('auth.errors.fillAll'));
      return;
    }
    if (pw !== cpw) {
      setError(t('auth.errors.passwordMismatch'));
      return;
    }
    if (pw.length < 6) {
      setError(t('auth.errors.passwordMin'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      await createUserWithEmailAndPassword(auth, em, pw);
      handleClose();
    } catch (err) {
      console.error('Email sign-up error:', err);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const em = norm(email);

    if (!em) {
      setError(t('auth.errors.enterEmail'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      setMessage('');
      await sendPasswordResetEmail(auth, em);
      setMessage(t('auth.messages.resetSent'));
    } catch (err) {
      console.error('Password reset error:', err);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const title =
    mode === 'signin'
      ? t('auth.titles.signin')
      : mode === 'signup'
      ? t('auth.titles.signup')
      : t('auth.titles.forgot');

  const subtitle =
    mode === 'signin'
      ? t('auth.subtitles.signin')
      : mode === 'signup'
      ? t('auth.subtitles.signup')
      : t('auth.subtitles.forgot');

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-title"
    >
      <div className="bg-white rounded-lg max-w-md w-full p-6 relative shadow-xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          disabled={loading}
          aria-label={t('auth.actions.close')}
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4" aria-hidden="true">
            <div className="bg-blue-100 p-3 rounded-full">
              <User className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h2 id="auth-title" className="text-2xl font-bold text-gray-900 mb-2">
            {title}
          </h2>
          <p className="text-gray-600">{subtitle}</p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4"
            role="alert"
          >
            {error}
          </div>
        )}
        {message && (
          <div
            className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-4"
            role="status"
          >
            {message}
          </div>
        )}

        {/* Google Sign-In Button */}
        {mode !== 'forgot' && (
          <div className="mb-6">
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t('auth.actions.continueWithGoogle')}
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? t('auth.actions.signingIn') : t('auth.actions.continueWithGoogle')}
            </button>
          </div>
        )}

        {/* Divider */}
        {mode !== 'forgot' && (
          <div className="relative mb-6" role="separator" aria-label={t('auth.labels.orEmail')}>
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                {t('auth.labels.orEmail')}
              </span>
            </div>
          </div>
        )}

        {/* Email/Password Form */}
        <form
          onSubmit={
            mode === 'signin' ? handleEmailSignIn :
            mode === 'signup' ? handleEmailSignUp :
            handleForgotPassword
          }
          noValidate
        >
          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="email">
              {t('auth.fields.email')}
            </label>
            <div className="relative">
              <Mail className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                placeholder={t('auth.placeholders.email')}
                disabled={loading}
                required
                aria-invalid={!!error && /email/i.test(error)}
              />
            </div>
          </div>

          {/* Password */}
          {mode !== 'forgot' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="password">
                {t('auth.fields.password')}
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  placeholder={t('auth.placeholders.password')}
                  disabled={loading}
                  required
                  minLength={6}
                  aria-invalid={!!error && /password/i.test(error)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={loading}
                  aria-label={showPassword ? t('auth.actions.hidePassword') : t('auth.actions.showPassword')}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          )}

          {/* Confirm Password */}
          {mode === 'signup' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="confirm">
                {t('auth.fields.confirmPassword')}
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                  id="confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  placeholder={t('auth.placeholders.confirmPassword')}
                  disabled={loading}
                  required
                  minLength={6}
                  aria-invalid={!!error && /match/i.test(error)}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={loading}
                  aria-label={showConfirmPassword ? t('auth.actions.hidePassword') : t('auth.actions.showPassword')}
                  aria-pressed={showConfirmPassword}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? t('auth.actions.pleaseWait')
              : mode === 'signin'
              ? t('auth.actions.signIn')
              : mode === 'signup'
              ? t('auth.actions.createAccount')
              : t('auth.actions.sendReset')}
          </button>
        </form>

        {/* Mode Switch */}
        <div className="mt-6 text-center text-sm">
          {mode === 'signin' && (
            <div className="space-y-2">
              <p className="text-gray-600">
                {t('auth.links.noAccount')}{' '}
                <button
                  onClick={() => handleModeChange('signup')}
                  className="text-blue-600 hover:underline font-medium"
                  disabled={loading}
                >
                  {t('auth.links.signUp')}
                </button>
              </p>
              <p>
                <button
                  onClick={() => handleModeChange('forgot')}
                  className="text-blue-600 hover:underline text-sm"
                  disabled={loading}
                >
                  {t('auth.links.forgot')}
                </button>
              </p>
            </div>
          )}

          {mode === 'signup' && (
            <p className="text-gray-600">
              {t('auth.links.haveAccount')}{' '}
              <button
                onClick={() => handleModeChange('signin')}
                className="text-blue-600 hover:underline font-medium"
                disabled={loading}
              >
                {t('auth.links.signIn')}
              </button>
            </p>
          )}

          {mode === 'forgot' && (
            <p className="text-gray-600">
              {t('auth.links.remembered')}{' '}
              <button
                onClick={() => handleModeChange('signin')}
                className="text-blue-600 hover:underline font-medium"
                disabled={loading}
              >
                {t('auth.links.backToSignIn')}
              </button>
            </p>
          )}
        </div>

        {/* Terms and Privacy (Sign Up only) */}
        {mode === 'signup' && (
          <div className="mt-4 text-xs text-gray-500 text-center">
            {t('auth.legal.prefix')}{' '}
            <a href="/terms-of-service" className="text-blue-600 hover:underline">
              {t('auth.legal.terms')}
            </a>{' '}
            {t('auth.legal.and')}{' '}
            <a href="/privacy" className="text-blue-600 hover:underline">
              {t('auth.legal.privacy')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
